import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import authService from '../../services/authService';
import { UpdateProfileData, UpdatePasswordData } from '../../services/authService';
import { addNotification } from './uiSlice';
import {
  clearDriveApiToken,
  getDriveApiToken,
  signOutDrive
} from '../../lib/auth-token';

interface AuthState {
  user: any | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  loading?: boolean;
  message?: string | null;
}

const initialState: AuthState = {
  user: null,
  token: getDriveApiToken(),
  isAuthenticated: false,
  isLoading: false,
  loading: false,
  error: null
};

export const loadUser = createAsyncThunk(
  'auth/loadUser',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      if (!getDriveApiToken()) {
        return rejectWithValue({ message: 'No token found', status: 401 });
      }
      return await authService.getProfile();
    } catch (error: any) {
      const status = error.response?.status;
      const errorMessage = error.response?.data?.message || 'Failed to load user';
      if (status === 401) {
        dispatch(
          addNotification({
            type: 'error',
            message: errorMessage,
            duration: 5000
          })
        );
      }
      return rejectWithValue({ message: errorMessage, status: status || 0 });
    }
  }
);

export const fetchProfile = loadUser;

export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  try {
    await authService.logout();
  } catch {
    /* ignore */
  }
  clearDriveApiToken();
  dispatch(
    addNotification({
      type: 'info',
      message: 'You have been logged out',
      duration: 5000
    })
  );
  return null;
});

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData: UpdateProfileData, { rejectWithValue, dispatch }) => {
    try {
      if (!getDriveApiToken()) return rejectWithValue('No token found');
      const result = await authService.updateProfile(profileData);
      dispatch(
        addNotification({
          type: 'success',
          message: 'Profile updated successfully!',
          duration: 5000
        })
      );
      return result;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      dispatch(
        addNotification({
          type: 'error',
          message: errorMessage,
          duration: 5000
        })
      );
      return rejectWithValue(errorMessage);
    }
  }
);

export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async (passwordData: UpdatePasswordData, { rejectWithValue, dispatch }) => {
    try {
      if (!getDriveApiToken()) return rejectWithValue('No token found');
      const result = await authService.updatePassword(passwordData);
      dispatch(
        addNotification({
          type: 'success',
          message: 'Password updated successfully!',
          duration: 5000
        })
      );
      return result;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update password';
      dispatch(
        addNotification({
          type: 'error',
          message: errorMessage,
          duration: 5000
        })
      );
      return rejectWithValue(errorMessage);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUser.pending, (state) => {
        state.isLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(loadUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.loading = false;
        state.isAuthenticated = true;
        state.token = getDriveApiToken();
        state.user = action.payload;
      })
      .addCase(loadUser.rejected, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        const payload = action.payload as
          | { message?: string; status?: number }
          | string
          | undefined;
        const status = typeof payload === 'object' && payload ? payload.status : undefined;
        const message =
          typeof payload === 'string'
            ? payload
            : payload?.message || 'Failed to load user';
        state.error = message;
        if (status === 401 || message === 'No token found') {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          clearDriveApiToken();
        }
      })
      .addCase(logout.fulfilled, (state) => {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updatePassword.pending, (state) => {
        state.isLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.loading = false;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearError } = authSlice.actions;
export { signOutDrive };
export default authSlice.reducer;
